import { getFileDetail } from 'stores/WorkSpaceStoreAdapter';

/**
 * CodeBlock の link 機能で使う「マイワークスペースのファイル一覧」の共有ストア。
 *
 * - KnzEditor が起動時に getWorkSpace() から算出してセットする。
 * - Monaco 補完（knzLanguage）が `link:` の候補一覧に使う。
 * - Monaco ホバー（knzLanguage）が `link:<fileId>` のパス表示に使う（ファイル取得APIで解決・キャッシュ）。
 *
 * モジュールレベルのミュータブルな参照にすることで、React のツリー外（Monaco 補完/ホバー）から
 * 参照できる。CodeBlock の link 表示は getFileDetail のレスポンス（name）を直接使うため
 * このストアには依存しない。
 */

/** link 補完・表示に使うファイル情報 */
export type LinkFile = {
  /** ファイル実体の id（link に埋め込む値） */
  fileId: string;
  /** ファイル名 */
  name: string;
  /** ディレクトリパス（`/` 区切り。補完の補助表示用） */
  dirPath: string;
};

let linkFiles: LinkFile[] = [];

/** マイワークスペースのファイル一覧を設定する（KnzEditor が呼ぶ） */
export const setLinkFiles = (files: LinkFile[]): void => {
  linkFiles = files;
};

/** ファイル一覧を取得する（Monaco の link: 補完が使う） */
export const getLinkFiles = (): LinkFile[] => linkFiles;

// --- fileId → 絶対パス の解決キャッシュ（Monaco ホバー用） ---

/**
 * fileId → 絶対パス（例: /src/Main.java）のキャッシュ。
 * ファイル取得API（API6106）で一度解決したら以降は即時に返す。
 * null は「取得できなかった（不在・権限なし等）」を表し、再取得を無駄に繰り返さない。
 */
const pathCache = new Map<string, string | null>();

/** 解決中の Promise を保持し、同一 fileId への重複リクエストをまとめる。 */
const inflight = new Map<string, Promise<string | null>>();

/** キャッシュ済みの絶対パスを同期取得する（未解決は undefined、取得失敗は null）。 */
export const getCachedLinkPath = (fileId: string): string | null | undefined => {
  return pathCache.get(fileId);
};

/**
 * fileId の絶対パスをファイル取得API（API6106）で解決する。
 * 解決済みならキャッシュから即時返す。取得失敗（不在・権限なし）は null をキャッシュして返す。
 * Monaco のホバープロバイダから await して使う。
 */
export const resolveLinkPath = async (fileId: string): Promise<string | null> => {
  if (pathCache.has(fileId)) return pathCache.get(fileId) ?? null;
  const existing = inflight.get(fileId);
  if (existing) return existing;

  const p = getFileDetail(fileId)
    .then((detail) => {
      const path = detail.path ?? null;
      pathCache.set(fileId, path);
      return path;
    })
    .catch(() => {
      // 不在・権限なし等はパス解決不可として null をキャッシュ（無駄な再取得を防ぐ）
      pathCache.set(fileId, null);
      return null;
    })
    .finally(() => {
      inflight.delete(fileId);
    });
  inflight.set(fileId, p);
  return p;
};

/** パス解決キャッシュをクリアする（別ワークスペースを開いたときなどに使う）。 */
export const clearLinkPathCache = (): void => {
  pathCache.clear();
  inflight.clear();
};
